import React from "react";
import { Link } from "react-router-dom";
import { Shield, Smartphone, Server, Database, Zap, FileCode2, ShoppingCart } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const features = [
  { icon: Smartphone, title: "Icônes style iPhone", desc: "Carrés arrondis monochromes uniformes, comme une page d'accueil iOS." },
  { icon: Shield, title: "Whitelist IP + 2FA TOTP", desc: "Restreignez l'accès par IP et activez l'authentification 2 facteurs (Google Authenticator)." },
  { icon: Zap, title: "Drag & drop + sous-catégories", desc: "Réordonnez vos liens, créez des sous-catégories, basculez dark/light." },
  { icon: FileCode2, title: "Import / Export CSV / JSON", desc: "Migrez vos liens depuis un tableur, ou exportez votre catalogue." },
  { icon: Server, title: "PHP 7.4+ ultra léger", desc: "Compatible tout hébergement mutualisé : OVH, Ionos, Hostinger, o2switch…" },
  { icon: Database, title: "MySQL / MariaDB", desc: "Import phpMyAdmin en un clic, ou installation automatique guidée." },
];

export default function PhpLandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16 text-center" data-testid="landing-hero">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">// Le portail interne nouvelle génération</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-6">
          Tous vos outils,<br />en un seul portail.
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
          <strong>WiLink</strong> est la solution clé en main pour centraliser les liens, applications et raccourcis internes de votre entreprise. Style iPhone, ultra léger, sécurisé.
        </p>

        {/* iPhone-style preview grid */}
        <div className="max-w-md mx-auto bg-card border border-border rounded-2xl p-8 mb-10 shadow-sm" data-testid="preview-grid">
          <div className="grid grid-cols-4 gap-x-4 gap-y-6">
            {[
              "fa-brands fa-slack", "fa-brands fa-github", "fa-brands fa-google-drive", "fa-brands fa-figma",
              "fa-brands fa-discord", "fa-brands fa-aws", "fa-solid fa-cloud", "fa-solid fa-shield-halved",
              "fa-solid fa-database", "fa-solid fa-terminal", "fa-solid fa-envelope", "fa-solid fa-calendar-days",
            ].map((cls, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-14 h-14 rounded-[22%] bg-primary text-white flex items-center justify-center relative overflow-hidden shadow-md"
                  style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.1), 0 8px 22px rgba(0,0,0,0.12)" }}
                >
                  <i className={`${cls} text-xl`} />
                  <span className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
                </div>
                <span className="text-[10px] text-muted-foreground">App</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/tarifs"
            className="h-12 px-6 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center gap-2"
            data-testid="hero-order-button"
          >
            <ShoppingCart className="h-5 w-5" /> Commander WiLink
          </Link>
          <a
            href="/demo"
            className="h-12 px-6 rounded-md border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-medium inline-flex items-center gap-2 transition-colors"
            data-testid="hero-demo-link"
          >
            <Zap className="h-5 w-5" /> Tester la démo en ligne
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-6">
          ⏱ Démo réinitialisée toutes les 30 minutes · Sans engagement · Téléchargement immédiat après paiement
        </p>
      </section>

      {/* DEMO PANEL */}
      <section className="max-w-6xl mx-auto px-6 py-12" data-testid="demo-section">
        <div className="border-2 border-primary rounded-xl bg-card p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3 font-semibold">// Démo live</p>
              <h3 className="text-3xl font-black tracking-tight mb-3">Essayez WiLink maintenant</h3>
              <p className="text-muted-foreground mb-6">
                Une démo complète et fonctionnelle. Naviguez dans les liens, connectez-vous au backoffice, créez des catégories, testez la whitelist IP. La démo est réinitialisée toutes les 30 minutes.
              </p>
              <div className="bg-muted/50 rounded-md p-4 mb-6 border border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Identifiants démo</p>
                <p className="font-mono text-sm">admin@netkom.fr</p>
                <p className="font-mono text-sm">admin123</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/demo"
                  className="h-11 px-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center gap-2"
                  data-testid="demo-public-link"
                >
                  Voir la page publique →
                </a>
                <a
                  href="/admin/login"
                  className="h-11 px-5 rounded-md border border-border hover:bg-accent font-medium inline-flex items-center gap-2"
                  data-testid="demo-admin-link"
                >
                  Accéder au backoffice →
                </a>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-border bg-muted/30 p-6">
              <div className="grid grid-cols-4 gap-3 mb-4">
                {["fa-brands fa-slack","fa-brands fa-github","fa-brands fa-discord","fa-solid fa-cloud","fa-brands fa-figma","fa-solid fa-database","fa-brands fa-aws","fa-solid fa-shield-halved"].map((cls,i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-[22%] bg-primary text-white flex items-center justify-center relative overflow-hidden"
                    style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 16px rgba(0,0,0,0.12)" }}
                  >
                    <i className={`${cls} text-lg`} />
                  </div>
                ))}
              </div>
              <div className="text-center text-xs text-muted-foreground">Aperçu du style &quot;iPhone&quot;</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-border" data-testid="features-section">
        <h2 className="text-3xl font-black tracking-tight mb-2">Fonctionnalités</h2>
        <p className="text-muted-foreground mb-10">Tout ce qu&apos;il faut pour gérer un portail interne professionnel.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="border border-border rounded-lg p-6 bg-card hover:shadow-md transition-shadow" data-testid={`feature-${f.title}`}>
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-bold mb-1">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pourquoi WiLink */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-border" data-testid="why-section">
        <h2 className="text-3xl font-black tracking-tight mb-10">Pourquoi WiLink ?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="border border-border rounded-lg p-6 bg-card">
            <h3 className="font-bold text-lg mb-2">⚡ Ultra léger</h3>
            <p className="text-sm text-muted-foreground">PHP/MySQL pur, sans framework lourd. Fonctionne sur n&apos;importe quel hébergement mutualisé à 3€/mois.</p>
          </div>
          <div className="border border-border rounded-lg p-6 bg-card">
            <h3 className="font-bold text-lg mb-2">🔒 Sécurisé</h3>
            <p className="text-sm text-muted-foreground">Whitelist IP, 2FA TOTP (Google Authenticator), CSRF, bcrypt — sécurité pro intégrée.</p>
          </div>
          <div className="border border-border rounded-lg p-6 bg-card">
            <h3 className="font-bold text-lg mb-2">🎨 Personnalisable</h3>
            <p className="text-sm text-muted-foreground">Votre logo, votre fond, votre charte. Sous-catégories, annonces, mode sombre, RDP… Tout est configurable.</p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="max-w-6xl mx-auto px-6 py-20 border-t border-border text-center" data-testid="cta-section">
        <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-6">Prêt à passer à WiLink ?</h2>
        <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
          Choisissez la formule adaptée à votre entreprise : auto-hébergé sur votre serveur ou en mode SaaS clé en main.
        </p>
        <Link
          to="/tarifs"
          className="h-14 px-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium inline-flex items-center gap-3"
          data-testid="cta-order-button"
        >
          <ShoppingCart className="h-5 w-5" /> Voir les tarifs
        </Link>
      </section>

      <PublicFooter />

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
    </div>
  );
}
